
import React, { useState, useEffect } from 'react';
import { Search, Save, Trash2, X, CheckCircle, FileText } from 'lucide-react';
import { supabase } from '../../lib/supabase';


// Interface for Enrollment similar to DB structure
interface Enrollment {
    id: string;
    student_id: string;
    course_name: string;
    term: string;
    start_date: string;
    end_date: string;
    schedule_description: string;
    format: string;
    status: string;
    enrollment_date?: string;
    is_paid?: boolean;
    payment_amount?: number;
    payment_method?: string;
    contract_sent?: boolean;
    student?: {
        first_name: string;
        last_name: string;
    }
}

interface Student {
    id: string;
    first_name: string;
    last_name: string;
}

const CourseEnrollments: React.FC = () => {
    // State
    const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
    const [students, setStudents] = useState<Student[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    // Editing State
    // We track edits by ID. If ID is 'new', it's the new row.
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editForm, setEditForm] = useState<Partial<Enrollment>>({});

    // New Row State
    const [isAddingNew, setIsAddingNew] = useState(false);
    const [newRowData, setNewRowData] = useState<Partial<Enrollment>>({
        status: 'active',
        format: 'physical',
        is_paid: false,
        contract_sent: false,
        enrollment_date: new Date().toISOString().split('T')[0]
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            // Fetch Enrollments
            const { data: enrollData, error: enrollError } = await supabase
                .from('testprep_enrollments')
                .select(`
                    *,
                    student:testprep_students (
                        first_name,
                        last_name
                    )
                `)
                .order('created_at', { ascending: false });

            if (enrollError) throw enrollError;
            setEnrollments(enrollData || []);

            // Fetch Students for Dropdown
            const { data: studentData, error: studentError } = await supabase
                .from('testprep_students')
                .select('id, first_name, last_name')
                .order('first_name');

            if (studentError) throw studentError;
            setStudents(studentData || []);

        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    // -- Handlers --

    const handleEditClick = (enrollment: Enrollment) => {
        setEditingId(enrollment.id);
        setEditForm(enrollment);
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setEditForm({});
    };

    const handleSaveEdit = async (id: string) => {
        try {
            const { error } = await supabase
                .from('testprep_enrollments')
                .update({
                    course_name: editForm.course_name,
                    term: editForm.term,
                    start_date: editForm.start_date || null,
                    end_date: editForm.end_date || null,
                    schedule_description: editForm.schedule_description,
                    format: editForm.format,
                    status: editForm.status,
                    enrollment_date: editForm.enrollment_date || null,
                    is_paid: editForm.is_paid || false,
                    payment_amount: editForm.payment_amount || 0,
                    payment_method: editForm.payment_method || null,
                    contract_sent: editForm.contract_sent || false
                })
                .eq('id', id);

            if (error) throw error;

            // Update local state
            setEnrollments(prev => prev.map(e => e.id === id ? { ...e, ...editForm } : e));
            setEditingId(null);
        } catch (error: any) {
            alert('Güncelleme hatası: ' + error.message);
        }
    };

    const handleDelete = async (id: string) => {
        if (!await window.appConfirm('Bu kaydı silmek istediğinize emin misiniz?')) return;
        try {
            const { error } = await supabase
                .from('testprep_enrollments')
                .delete()
                .eq('id', id);

            if (error) throw error;
            setEnrollments(prev => prev.filter(e => e.id !== id));
        } catch (error: any) {
            alert('Silme hatası: ' + error.message);
        }
    };

    const handleSaveNew = async () => {
        if (!newRowData.student_id || !newRowData.course_name) {
            alert('Lütfen öğrenci ve kurs adını giriniz.');
            return;
        }

        try {
            const { data, error } = await supabase
                .from('testprep_enrollments')
                .insert(newRowData)
                .select(`
                    *,
                    student:testprep_students (
                        first_name,
                        last_name
                    )
                `)
                .single();

            if (error) throw error;

            setEnrollments([data, ...enrollments]);
            setIsAddingNew(false);
            setNewRowData({ status: 'active', format: 'physical' });
        } catch (error: any) {
            alert('Kayıt hatası: ' + error.message);
        }
    };



    // Filter
    const filteredEnrollments = enrollments.filter(e => {
        const fullString = `${e.course_name} ${e.student?.first_name} ${e.student?.last_name} ${e.term}`.toLowerCase();
        return fullString.includes(searchTerm.toLowerCase());
    });

    return (
        <div className="space-y-6 h-full flex flex-col">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Kurs Kayıtları (Liste Görünümü)</h2>
                    <p className="text-gray-600 dark:text-gray-400">
                        Doğrudan liste üzerinden düzenleme yapabilir ve yeni kayıt ekleyebilirsiniz.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                        <input
                            type="text"
                            placeholder="Ara..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-9 pr-3 py-1.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                </div>
            </div>

            <div className="flex-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden flex flex-col shadow-sm">
                <div className="overflow-x-auto flex-1">
                    <table className="w-full text-sm text-left">
                        <thead className="bg-gray-50 dark:bg-gray-800 text-gray-500 font-medium border-b border-gray-200 dark:border-gray-700">
                            <tr>
                                <th className="px-4 py-3 w-48">Öğrenci</th>
                                <th className="px-4 py-3">Kurs Adı</th>
                                <th className="px-4 py-3 w-28">Kayıt Tarihi</th>
                                <th className="px-4 py-3 w-28">Dönem</th>
                                <th className="px-4 py-3 w-28">Başlangıç</th>
                                <th className="px-4 py-3 w-28">Bitiş</th>
                                <th className="px-4 py-3 w-24">Format</th>
                                <th className="px-4 py-3 w-24">Tutar</th>
                                <th className="px-4 py-3 w-24">Ödeme</th>
                                <th className="px-4 py-3 w-28">Ödeme Şekli</th>
                                <th className="px-4 py-3 w-32">Sözleşme</th>
                                <th className="px-4 py-3 w-24">Durum</th>
                                <th className="px-4 py-3 w-20 text-center">İşlem</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {/* New Row Input Area (Toggleable) */}
                            {isAddingNew ? (
                                <tr className="bg-blue-50 dark:bg-blue-900/10">
                                    <td className="p-2">
                                        <select
                                            className="w-full bg-white dark:bg-gray-800 border border-blue-300 rounded px-2 py-1 text-xs"
                                            value={newRowData.student_id || ''}
                                            onChange={e => setNewRowData({ ...newRowData, student_id: e.target.value })}
                                        >
                                            <option value="">Seçiniz...</option>
                                            {students.map(s => (
                                                <option key={s.id} value={s.id}>{s.first_name} {s.last_name}</option>
                                            ))}
                                        </select>
                                    </td>
                                    <td className="p-2">
                                        <input
                                            className="w-full bg-white dark:bg-gray-800 border border-blue-300 rounded px-2 py-1 text-xs"
                                            placeholder="Kurs Adı"
                                            value={newRowData.course_name || ''}
                                            onChange={e => setNewRowData({ ...newRowData, course_name: e.target.value })}
                                        />
                                    </td>
                                    <td className="p-2">
                                        <input
                                            type="date"
                                            className="w-full bg-white dark:bg-gray-800 border border-blue-300 rounded px-2 py-1 text-xs"
                                            value={newRowData.enrollment_date || ''}
                                            onChange={e => setNewRowData({ ...newRowData, enrollment_date: e.target.value })}
                                        />
                                    </td>
                                    <td className="p-2">
                                        <input
                                            className="w-full bg-white dark:bg-gray-800 border border-blue-300 rounded px-2 py-1 text-xs"
                                            placeholder="Dönem"
                                            value={newRowData.term || ''}
                                            onChange={e => setNewRowData({ ...newRowData, term: e.target.value })}
                                        />
                                    </td>
                                    <td className="p-2">
                                        <input
                                            type="date"
                                            className="w-full bg-white dark:bg-gray-800 border border-blue-300 rounded px-2 py-1 text-xs"
                                            value={newRowData.start_date || ''}
                                            onChange={e => setNewRowData({ ...newRowData, start_date: e.target.value })}
                                        />
                                    </td>
                                    <td className="p-2">
                                        <input
                                            type="date"
                                            className="w-full bg-white dark:bg-gray-800 border border-blue-300 rounded px-2 py-1 text-xs"
                                            value={newRowData.end_date || ''}
                                            onChange={e => setNewRowData({ ...newRowData, end_date: e.target.value })}
                                        />
                                    </td>
                                    <td className="p-2">
                                        <select
                                            className="w-full bg-white dark:bg-gray-800 border border-blue-300 rounded px-2 py-1 text-xs"
                                            value={newRowData.format}
                                            onChange={e => setNewRowData({ ...newRowData, format: e.target.value })}
                                        >
                                            <option value="physical">Yüz Yüze</option>
                                            <option value="online">Online</option>
                                            <option value="hybrid">Hibrit</option>
                                        </select>
                                    </td>
                                    <td className="p-2">
                                        <input
                                            type="number"
                                            className="w-full bg-white dark:bg-gray-800 border border-blue-300 rounded px-2 py-1 text-xs"
                                            placeholder="Tutar"
                                            value={newRowData.payment_amount || ''}
                                            onChange={e => setNewRowData({ ...newRowData, payment_amount: parseFloat(e.target.value) || 0 })}
                                        />
                                    </td>
                                    <td className="p-2">
                                        <select
                                            className="w-full bg-white dark:bg-gray-800 border border-blue-300 rounded px-2 py-1 text-xs"
                                            value={newRowData.is_paid ? 'true' : 'false'}
                                            onChange={e => setNewRowData({ ...newRowData, is_paid: e.target.value === 'true' })}
                                        >
                                            <option value="false">Ödenmedi</option>
                                            <option value="true">Ödendi</option>
                                        </select>
                                    </td>
                                    <td className="p-2">
                                        <select
                                            className="w-full bg-white dark:bg-gray-800 border border-blue-300 rounded px-2 py-1 text-xs"
                                            value={newRowData.payment_method || ''}
                                            onChange={e => setNewRowData({ ...newRowData, payment_method: e.target.value })}
                                        >
                                            <option value="">Seçiniz</option>
                                            <optgroup label="Nakit">
                                                <option value="Nakit - Bireysel hesap">Bireysel hesap</option>
                                                <option value="Nakit - Skyland ofis">Skyland ofis</option>
                                                <option value="Nakit - Ankara ofis">Ankara ofis</option>
                                                <option value="Nakit - Bursa Ofis">Bursa Ofis</option>
                                                <option value="Nakit - İzmir Ofis">İzmir Ofis</option>
                                                <option value="Nakit - Kadıköy Ofis">Kadıköy Ofis</option>
                                                <option value="Nakit - Fethiye Ofis">Fethiye Ofis</option>
                                            </optgroup>
                                            <optgroup label="Kredi Kartı">
                                                <option value="Kredi Kartı - Pisa Edu Moka">Pisa Edu Moka</option>
                                                <option value="Kredi Kartı - Pisa Test Prep Moka">Pisa Test Prep Moka</option>
                                            </optgroup>
                                            <optgroup label="Şirket">
                                                <option value="Şirket - Pisa Edu">Pisa Edu</option>
                                                <option value="Şirket - Pisa Test Prep">Pisa Test Prep</option>
                                            </optgroup>
                                            <option value="havale">Havale/EFT</option>
                                            <option value="taksit">Taksit</option>
                                        </select>
                                    </td>
                                    <td className="p-2">
                                        <select
                                            className="w-full bg-white dark:bg-gray-800 border border-blue-300 rounded px-2 py-1 text-xs"
                                            value={newRowData.contract_sent ? 'true' : 'false'}
                                            onChange={e => setNewRowData({ ...newRowData, contract_sent: e.target.value === 'true' })}
                                        >
                                            <option value="false">Hayır</option>
                                            <option value="true">Evet</option>
                                        </select>
                                    </td>
                                    <td className="p-2">
                                        <select
                                            className="w-full bg-white dark:bg-gray-800 border border-blue-300 rounded px-2 py-1 text-xs"
                                            value={newRowData.status}
                                            onChange={e => setNewRowData({ ...newRowData, status: e.target.value })}
                                        >
                                            <option value="active">Aktif</option>
                                            <option value="completed">Tamamlandı</option>
                                            <option value="dropped">Bıraktı</option>
                                        </select>
                                    </td>
                                    <td className="p-2 text-center flex gap-1 justify-center">
                                        <button onClick={handleSaveNew} className="p-1 text-green-600 hover:bg-green-100 rounded">
                                            <Save className="w-4 h-4" />
                                        </button>
                                        <button onClick={() => setIsAddingNew(false)} className="p-1 text-red-600 hover:bg-red-100 rounded">
                                            <X className="w-4 h-4" />
                                        </button>
                                    </td>
                                </tr>
                            ) : (
                                <tr
                                    className="hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer border-b border-gray-100 dark:border-gray-800"
                                    onClick={() => setIsAddingNew(true)}
                                >
                                    <td colSpan={12} className="p-2 text-center text-blue-600 font-medium bg-gray-50 dark:bg-gray-800/50 hover:bg-blue-50 transition-colors">
                                        + Yeni Satır Ekle
                                    </td>
                                </tr>
                            )}

                            {/* Existing Rows */}
                            {loading ? (
                                <tr><td colSpan={12} className="p-8 text-center">Yükleniyor...</td></tr>
                            ) : filteredEnrollments.map(item => {
                                const isEditing = editingId === item.id;
                                const rowBgClass = item.is_paid
                                    ? 'bg-green-50/50 dark:bg-green-900/10 hover:bg-green-100/50 dark:hover:bg-green-900/20'
                                    : 'bg-red-50/30 dark:bg-red-900/10 hover:bg-red-100/50 dark:hover:bg-red-900/20';
                                return (
                                    <tr key={item.id} className={`${rowBgClass} group`}>
                                        <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">
                                            {item.student?.first_name} {item.student?.last_name}
                                        </td>

                                        {/* Kurs Adı */}
                                        <td className="p-0">
                                            {isEditing ? (
                                                <input
                                                    className="w-full h-full p-3 bg-blue-50 dark:bg-blue-900/10 outline-none text-sm"
                                                    value={editForm.course_name}
                                                    onChange={e => setEditForm({ ...editForm, course_name: e.target.value })}
                                                />
                                            ) : (
                                                <div onClick={() => handleEditClick(item)} className="p-3 w-full h-full cursor-text text-sm">{item.course_name}</div>
                                            )}
                                        </td>

                                        {/* Kayıt Tarihi */}
                                        <td className="p-0">
                                            {isEditing ? (
                                                <input
                                                    type="date"
                                                    className="w-full h-full p-3 bg-blue-50 dark:bg-blue-900/10 outline-none text-xs"
                                                    value={editForm.enrollment_date || ''}
                                                    onChange={e => setEditForm({ ...editForm, enrollment_date: e.target.value })}
                                                />
                                            ) : (
                                                <div onClick={() => handleEditClick(item)} className="p-3 w-full h-full cursor-text text-xs">{item.enrollment_date}</div>
                                            )}
                                        </td>

                                        {/* Dönem */}
                                        <td className="p-0">
                                            {isEditing ? (
                                                <input
                                                    className="w-full h-full p-3 bg-blue-50 dark:bg-blue-900/10 outline-none text-xs"
                                                    value={editForm.term || ''}
                                                    onChange={e => setEditForm({ ...editForm, term: e.target.value })}
                                                />
                                            ) : (
                                                <div onClick={() => handleEditClick(item)} className="p-3 w-full h-full cursor-text text-xs">{item.term}</div>
                                            )}
                                        </td>

                                        {/* Başlangıç */}
                                        <td className="p-0">
                                            {isEditing ? (
                                                <input
                                                    type="date"
                                                    className="w-full h-full p-3 bg-blue-50 dark:bg-blue-900/10 outline-none text-xs"
                                                    value={editForm.start_date || ''}
                                                    onChange={e => setEditForm({ ...editForm, start_date: e.target.value })}
                                                />
                                            ) : (
                                                <div onClick={() => handleEditClick(item)} className="p-3 w-full h-full cursor-text text-xs">{item.start_date}</div>
                                            )}
                                        </td>

                                        {/* Bitiş */}
                                        <td className="p-0">
                                            {isEditing ? (
                                                <input
                                                    type="date"
                                                    className="w-full h-full p-3 bg-blue-50 dark:bg-blue-900/10 outline-none text-xs"
                                                    value={editForm.end_date || ''}
                                                    onChange={e => setEditForm({ ...editForm, end_date: e.target.value })}
                                                />
                                            ) : (
                                                <div onClick={() => handleEditClick(item)} className="p-3 w-full h-full cursor-text text-xs">{item.end_date}</div>
                                            )}
                                        </td>

                                        {/* Format */}
                                        <td className="p-0">
                                            {isEditing ? (
                                                <select
                                                    className="w-full h-full p-3 bg-blue-50 dark:bg-blue-900/10 outline-none appearance-none text-xs"
                                                    value={editForm.format}
                                                    onChange={e => setEditForm({ ...editForm, format: e.target.value })}
                                                >
                                                    <option value="physical">Yüz Yüze</option>
                                                    <option value="online">Online</option>
                                                    <option value="hybrid">Hibrit</option>
                                                </select>
                                            ) : (
                                                <div onClick={() => handleEditClick(item)} className="p-3 w-full h-full cursor-pointer text-xs">
                                                    {item.format === 'physical' ? 'Yüz Yüze' : item.format === 'online' ? 'Online' : 'Hibrit'}
                                                </div>
                                            )}
                                        </td>

                                        {/* Ödeme Durumu */}
                                        <td className="p-0">
                                            {isEditing ? (
                                                <select
                                                    className="w-full h-full p-3 bg-blue-50 dark:bg-blue-900/10 outline-none appearance-none text-xs"
                                                    value={editForm.is_paid ? 'true' : 'false'}
                                                    onChange={e => setEditForm({ ...editForm, is_paid: e.target.value === 'true' })}
                                                >
                                                    <option value="false">Ödenmedi</option>
                                                    <option value="true">Ödendi</option>
                                                </select>
                                            ) : (
                                                <div onClick={() => handleEditClick(item)} className="p-3 w-full h-full cursor-pointer">
                                                    <span className={`px-2 py-1 text-xs rounded-full font-medium ${item.is_paid
                                                        ? 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300'
                                                        : 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-300'}`}>
                                                        {item.is_paid ? 'Ödendi' : 'Ödenmedi'}
                                                    </span>
                                                </div>
                                            )}
                                        </td>

                                        <td className="p-0">
                                            {isEditing ? (
                                                <input
                                                    type="number"
                                                    className="w-full h-full p-3 bg-blue-50 dark:bg-blue-900/10 outline-none text-xs"
                                                    placeholder="Tutar"
                                                    value={editForm.payment_amount || ''}
                                                    onChange={e => setEditForm({ ...editForm, payment_amount: parseFloat(e.target.value) || 0 })}
                                                />
                                            ) : (
                                                <div onClick={() => handleEditClick(item)} className="p-3 w-full h-full cursor-text text-xs">
                                                    {item.payment_amount ? `₺${item.payment_amount.toLocaleString()}` : '-'}
                                                </div>
                                            )}
                                        </td>

                                        {/* Ödeme Şekli */}
                                        <td className="p-0">
                                            {isEditing ? (
                                                <select
                                                    className="w-full h-full p-3 bg-blue-50 dark:bg-blue-900/10 outline-none appearance-none text-xs"
                                                    value={editForm.payment_method || ''}
                                                    onChange={e => setEditForm({ ...editForm, payment_method: e.target.value })}
                                                >
                                                    <option value="">Seçiniz</option>
                                                    <optgroup label="Nakit">
                                                        <option value="Nakit - Bireysel hesap">Bireysel hesap</option>
                                                        <option value="Nakit - Skyland ofis">Skyland ofis</option>
                                                        <option value="Nakit - Ankara ofis">Ankara ofis</option>
                                                        <option value="Nakit - Bursa Ofis">Bursa Ofis</option>
                                                        <option value="Nakit - İzmir Ofis">İzmir Ofis</option>
                                                        <option value="Nakit - Kadıköy Ofis">Kadıköy Ofis</option>
                                                        <option value="Nakit - Fethiye Ofis">Fethiye Ofis</option>
                                                    </optgroup>
                                                    <optgroup label="Kredi Kartı">
                                                        <option value="Kredi Kartı - Pisa Edu Moka">Pisa Edu Moka</option>
                                                        <option value="Kredi Kartı - Pisa Test Prep Moka">Pisa Test Prep Moka</option>
                                                    </optgroup>
                                                    <optgroup label="Şirket">
                                                        <option value="Şirket - Pisa Edu">Pisa Edu</option>
                                                        <option value="Şirket - Pisa Test Prep">Pisa Test Prep</option>
                                                    </optgroup>
                                                    <option value="havale">Havale/EFT</option>
                                                    <option value="taksit">Taksit</option>
                                                </select>
                                            ) : (
                                                <div onClick={() => handleEditClick(item)} className="p-3 w-full h-full cursor-text text-xs">
                                                    {item.payment_method || '-'}
                                                </div>
                                            )}
                                        </td>

                                        {/* Sözleşme */}
                                        <td className="p-0">
                                            {isEditing ? (
                                                <select
                                                    className="w-full h-full p-3 bg-blue-50 dark:bg-blue-900/10 outline-none appearance-none text-xs"
                                                    value={editForm.contract_sent ? 'true' : 'false'}
                                                    onChange={e => setEditForm({ ...editForm, contract_sent: e.target.value === 'true' })}
                                                >
                                                    <option value="false">Hayır</option>
                                                    <option value="true">Evet</option>
                                                </select>
                                            ) : (
                                                <div onClick={() => handleEditClick(item)} className="p-3 w-full h-full cursor-pointer flex items-center justify-center">
                                                    {item.contract_sent ? (
                                                        <CheckCircle className="w-4 h-4 text-green-600" />
                                                    ) : (
                                                        <FileText className="w-4 h-4 text-gray-400" />
                                                    )}
                                                </div>
                                            )}
                                        </td>

                                        {/* Durum */}
                                        <td className="p-0">
                                            {isEditing ? (
                                                <select
                                                    className="w-full h-full p-3 bg-blue-50 dark:bg-blue-900/10 outline-none appearance-none text-xs"
                                                    value={editForm.status}
                                                    onChange={e => setEditForm({ ...editForm, status: e.target.value })}
                                                >
                                                    <option value="active">Aktif</option>
                                                    <option value="completed">Tamamlandı</option>
                                                    <option value="dropped">Bıraktı</option>
                                                </select>
                                            ) : (
                                                <div onClick={() => handleEditClick(item)} className="p-3 w-full h-full cursor-pointer">
                                                    <span className={`px-2 py-1 text-xs rounded-full ${item.status === 'active' ? 'bg-blue-100 text-blue-700' :
                                                        item.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                                                        {item.status === 'active' ? 'Aktif' : item.status === 'completed' ? 'Tamamlandı' : 'Bıraktı'}
                                                    </span>
                                                </div>
                                            )}
                                        </td>

                                        <td className="px-4 py-3 text-center">
                                            {isEditing ? (
                                                <div className="flex gap-1 justify-center">
                                                    <button onClick={() => handleSaveEdit(item.id)} className="text-green-600 hover:bg-green-50 p-1 rounded">
                                                        <Save className="w-4 h-4" />
                                                    </button>
                                                    <button onClick={handleCancelEdit} className="text-gray-500 hover:bg-gray-100 p-1 rounded">
                                                        <X className="w-4 h-4" />
                                                    </button>
                                                </div>
                                            ) : (
                                                <button
                                                    onClick={() => handleDelete(item.id)}
                                                    className="text-red-400 hover:text-red-600 p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default CourseEnrollments;
